import { Injectable, inject } from '@angular/core';
import { AITool } from '../tool.decorator';
import { PresentationService } from '@omni/services/presentation/presentation.service';
import { ResourceService } from '@omni/services/resource/resource.service';

/**
 * Per-item detail readouts for sales presentations and resources. Counterpart
 * to the search tools — these expand one entity at a time into a richer DTO
 * (brands, specialties, page list, etc.).
 */
@Injectable({ providedIn: 'root' })
export class ContentTools {
  private readonly presentationService = inject(PresentationService);
  private readonly resourceService = inject(ResourceService);

  @AITool('get_presentation_details', 'Get detailed information about a specific presentation', [
    {
      name: 'presentation_id',
      type: 'string',
      description: 'The ID of the presentation to get details for',
      required: true,
    },
  ])
  async getPresentationDetails(presentation_id: string): Promise<any> {
    try {
      const presentations = this.presentationService.presentation || [];
      const presentation = presentations.find((pres: any) =>
        pres.ioPresentationId === presentation_id || pres.contentId === presentation_id,
      ) as any;

      if (!presentation) {
        return { success: false, message: `Presentation not found with ID: ${presentation_id}` };
      }

      const detailedInfo = {
        id: presentation.ioPresentationId || presentation.contentId,
        name: presentation.name,
        description: presentation.description || '',
        brands:
          presentation.brands?.map((b: any) => ({
            id: b.ID,
            name: b.name,
          })) || [],
        specialties:
          presentation.specialties?.map((s: any) => ({
            id: s.id,
            name: s.name,
          })) || [],
        therapeuticAreas: presentation.theraputicAreas || [],
        language: presentation.languageName || '',
        pageCount: presentation.presentationPages?.length || 0,
        // First 5 pages keeps payload small for the model.
        pages:
          presentation.presentationPages?.map((page: any) => ({
            id: page.id,
            name: page.name,
            order: page.pageSequence,
          })).slice(0, 5) || [],
        keyMessages: presentation.keyMessages?.map((km: any) => km.keyMessageName).slice(0, 3) || [],
        isDownloaded: presentation.downloaded || false,
        isFavorite: presentation.favourite || false,
        isNew: presentation.isNew || false,
        dimensions: {
          width: presentation.width,
          height: presentation.height,
          isResponsive: presentation.isResponsive,
        },
        availableFrom: presentation.availableFrom,
        availableUntil: presentation.availableUntil,
      };

      return { success: true, presentation: detailedInfo };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get presentation details' };
    }
  }

  @AITool('get_resource_details', 'Get detailed information about a specific resource', [
    { name: 'resource_id', type: 'string', description: 'The ID of the resource to get details for', required: true },
  ])
  async getResourceDetails(resource_id: string): Promise<any> {
    try {
      const resources = this.resourceService.allResources || [];
      const resource = resources.find((res: any) =>
        res.ioResourceId === resource_id || res.ioDocumentId === resource_id,
      ) as any;

      if (!resource) {
        return { success: false, message: `Resource not found with ID: ${resource_id}` };
      }

      const detailedInfo = {
        id: resource.ioResourceId || resource.ioDocumentId,
        name: resource.title || resource.name,
        description: resource.description || '',
        assetType: resource.assetType || resource.fileType || '',
        assetURL: resource.assetURL || '',
        thumbnailURL: resource.thumbnailURL || '',
        brands:
          resource.brands?.map((b: any) => ({
            id: b.ID,
            name: b.name,
          })) || [],
        specialties:
          resource.specialties?.map((s: any) => ({
            id: s.id,
            name: s.name,
          })) || [],
        therapeuticAreas: resource.therapeuticAreas || [],
        isDownloaded: resource.downloaded || false,
        createdOn: resource.createdOn,
        modifiedOn: resource.modifiedOn,
      };

      return { success: true, resource: detailedInfo };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get resource details' };
    }
  }
}
